<template>
  <div ref="seccionContadorRef" class="seccion-editorial-container">
    
    <!-- 1. MOÑO SUPERIOR FULL-WIDTH (EDGE-TO-EDGE) -->
    <div ref="monioRef" class="monio-full-width-wrapper">
      <img 
        src="@/assets/monio.png" 
        alt="Moño Rojo" 
        class="monio-img"
      >
    </div>

    <!-- 2. LAYOUT PRINCIPAL DESACOPLADO -->
    <div class="contenido-editorial-wrapper">

      <!-- BLOQUE DE TEXTO E INFORMACIÓN (INDEPENDIENTE) -->
      <div class="info-columna">
        
        <!-- TITULO EDITORIAL -->
        <div ref="headerTextRef" class="header-text-block">
          <h2 class="the-details-title">Falta Muy Poco</h2>
          <div class="linea-decorativa-gold"></div>
        </div>

        <!-- FRASE POÉTICA -->
        <div ref="fraseRef" class="frase-container">
          <p class="frase-linea">¡CADA SEGUNDO MÁS CERCA</p>
          <p class="frase-linea">DE MI GRAN DÍA!</p>
        </div>

        <!-- TITULO CONTADOR -->
        <div ref="tituloContadorRef" class="titulo-contador">
          <span class="solo-text">Solo</span>
          <span class="faltan-text">FALTAN</span>
        </div>

        <!-- CONTADOR MINIMALISTA (SIN CUADROS) -->
        <div ref="gridNumerosRef" class="contador-limpio-grid">
          <div class="tiempo-item">
            <span class="numero">{{ tiempo.dias }}</span>
            <span class="etiqueta">Días</span>
          </div>
          <div class="tiempo-item">
            <span class="numero">{{ tiempo.horas }}</span>
            <span class="etiqueta">Horas</span>
          </div>
          <div class="tiempo-item">
            <span class="numero">{{ tiempo.minutos }}</span>
            <span class="etiqueta">Minutos</span>
          </div>
          <div class="tiempo-item">
            <span class="numero">{{ tiempo.segundos }}</span>
            <span class="etiqueta">Segundos</span>
          </div>
        </div>

      </div>

      <!-- MUÑECA ANCLADA AL BORDE IZQUIERDO Y AL FONDO -->
      <div ref="muniecaRef" class="munieca-container">
        <img 
          src="@/assets/quinceañera.png" 
          alt="Quinceañera" 
          class="munieca-img"
        >
      </div>

    </div>

  </div>
</template>

<script>
import { ref, reactive, onMounted, onUnmounted } from 'vue'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

export default {
  name: 'ContadorEditorialLimpio',
  setup() {
    const seccionContadorRef = ref(null)
    const monioRef = ref(null)
    const muniecaRef = ref(null)
    const headerTextRef = ref(null)
    const fraseRef = ref(null)
    const tituloContadorRef = ref(null)
    const gridNumerosRef = ref(null)

    // Usamos un objeto REACTIVE. Es indestructible y Vue siempre lo renderizará bien en la plantilla
    const tiempo = reactive({
      dias: '00',
      horas: '00',
      minutos: '00',
      segundos: '00'
    })

    let timerId = null

    const iniciarCuentaAtras = () => {
      const fechaEvento = new Date(2026, 10, 28, 18, 0, 0).getTime()

      const calcularTiempo = () => {
        const ahora = Date.now()
        const dif = fechaEvento - ahora

        if (dif <= 0) {
          tiempo.dias = '00'
          tiempo.horas = '00'
          tiempo.minutos = '00'
          tiempo.segundos = '00'
          if (timerId) clearInterval(timerId)
          return
        }

        const d = Math.floor(dif / (1000 * 60 * 60 * 24))
        const h = Math.floor((dif / (1000 * 60 * 60)) % 24)
        const m = Math.floor((dif / (1000 * 60)) % 60)
        const s = Math.floor((dif / 1000) % 60)

        tiempo.dias = String(d).padStart(2, '0')
        tiempo.horas = String(h).padStart(2, '0')
        tiempo.minutos = String(m).padStart(2, '0')
        tiempo.segundos = String(s).padStart(2, '0')
      }

      calcularTiempo()
      if (timerId) clearInterval(timerId)
      timerId = setInterval(calcularTiempo, 1000)
    }

    onMounted(() => {
      // 1. Iniciar el reloj de inmediato
      iniciarCuentaAtras()

      // 2. Animación suave con GSAP sin tocar las etiquetas de texto de los números
      try {
        if (!seccionContadorRef.value) return

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: seccionContadorRef.value,
            start: 'top 85%',
            toggleActions: 'play none none none'
          }
        })

        if (monioRef.value) {
          tl.from(monioRef.value, { opacity: 0, y: -25, duration: 1, ease: 'power2.out' })
        }

        if (headerTextRef.value) {
          tl.from(headerTextRef.value, { opacity: 0, y: 20, duration: 0.8, ease: 'power2.out' }, '-=0.6')
        }

        if (fraseRef.value) {
          tl.from(fraseRef.value, { opacity: 0, y: 15, duration: 0.7, ease: 'power2.out' }, '-=0.5')
        }

        if (tituloContadorRef.value) {
          tl.from(tituloContadorRef.value, { opacity: 0, scale: 0.95, duration: 0.6, ease: 'back.out(1.2)' }, '-=0.4')
        }

        if (gridNumerosRef.value) {
          tl.from(gridNumerosRef.value, { opacity: 0, y: 20, duration: 0.6, ease: 'power2.out' }, '-=0.3')
        }

        if (muniecaRef.value) {
          tl.from(muniecaRef.value, { opacity: 0, x: -40, duration: 1.1, ease: 'power3.out' }, '-=0.8')
        }
      } catch (e) {
        // Si GSAP falla en algún otro componente, este catch evita que rompa el contador
      }
    })

    onUnmounted(() => {
      if (timerId) clearInterval(timerId)
    })

    return {
      seccionContadorRef,
      monioRef,
      muniecaRef,
      headerTextRef,
      fraseRef,
      tituloContadorRef,
      gridNumerosRef,
      tiempo
    }
  }
}
</script>

<style scoped>
/* ==========================================
   1. CONTENEDOR GENERAL EDGE-TO-EDGE
   ========================================== */
.seccion-editorial-container {
  position: relative;
  width: 100vw;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  min-height: 100vh;
  min-height: 100dvh;
  background-color: #ffffff;
  overflow: hidden;
  box-sizing: border-box;
  display: flex;
  margin-top:-80px;
  margin-bottom: 130px;
  flex-direction: column;
}

/* ==========================================
   2. MOÑO SUPERIOR SIN MARGENES
   ========================================== */
.monio-full-width-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  overflow: hidden;
  z-index: 5;
}

.monio-img {
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
}

/* ==========================================
   3. ESTRUCTURA EDITORIAL FLUIDA
   ========================================== */
.contenido-editorial-wrapper {
  position: relative;
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-start;
  padding-top: 1.5vh;
  box-sizing: border-box;
}

.info-columna {
  position: relative;
  z-index: 3;
  width: 60vw;
  margin-top: -4vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding-right: 6vw;
  box-sizing: border-box;
}

.munieca-container {
  position: absolute;
  bottom: 0;
  top: -5vh;
  left: -80vw;
  height: 65vh;
  max-height: 520px;
  z-index: 1;
  pointer-events: none;
}

.munieca-img {
  height: 100%;
  width: auto;
  display: block;
  object-fit: contain;
  filter: drop-shadow(4px 6px 12px rgba(0, 0, 0, 0.08));
}

/* ==========================================
   4. TIPOGRAFÍAS Y ESTILOS CON CLAMP
   ========================================== */
.header-text-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 1.2vh;
}

.the-details-title {
  font-family: 'ludovica', serif;
  font-size: clamp(1.8rem, 8vw, 2.8rem);
  color: #B28F60;
  font-weight: 400;
  margin: 0;
  line-height: 1.1;
  white-space: nowrap;
}

.linea-decorativa-gold {
  width: clamp(25px, 8vw, 40px);
  height: 1px;
  background: #B28F60;
  margin-top: 0.4vh;
  opacity: 0.5;
}

.frase-container {
  margin-bottom: 1.4vh;
}

.frase-linea {
  font-family: 'Playfair Display', serif;
  font-size: clamp(0.6rem, 2.6vw, 0.75rem);
  color: #B28F60;
  margin: 0;
  line-height: 1.4;
  font-weight: 600;
  letter-spacing: 0.1em;
}

.titulo-contador {
  margin-bottom: 1.2vh;
}

.solo-text {
  font-family: 'ludovica', serif;
  font-size: clamp(1.4rem, 6vw, 2rem);
  color: #B28F60;
  display: block;
  line-height: 1;
}

.faltan-text {
  font-family: 'Playfair Display', serif;
  font-size: clamp(0.85rem, 3.8vw, 1.1rem);
  color: #ee93bd;
  font-weight: 700;
  letter-spacing: 0.15em;
  display: block;
  margin-top: -0.2vh;
}

/* ==========================================
   5. CONTENEDOR CONTADOR
   ========================================== */
.contador-limpio-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 4vw;
  row-gap: 1.2vh;
  width: 100%;
  max-width: 45vw;
}

.tiempo-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: transparent;
  border: none;
  box-shadow: none;
}

.numero {
  font-family: 'Playfair Display', serif;
  font-size: clamp(1.5rem, 7vw, 2.2rem);
  font-weight: 600;
  color: #ee93bd;
  line-height: 1;
}

.etiqueta {
  font-family: 'Playfair Display', serif;
  font-size: clamp(0.55rem, 2.3vw, 0.7rem);
  color: #B28F60;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-top: 0.4vh;
  font-weight: 500;
}
</style>